import {
  OverrideChangeRequestByIdDocument,
  UpdateApproverResponsesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface OverrideChangeRequestInput {
  changeRequestId: string;
  /**
   * Rationale recorded as the CR Comment. MANDATORY (COI-658) — the router
   * input is `z.string().trim().min(1)`, so a null or blank rationale is a 400.
   * This is the one action in the approval lifecycle whose entire purpose is
   * recording why a human bypassed the chain, and V2 required it too.
   */
  comment: string;
  /** `true` → force-approve (+merge); `false` → force-reject. */
  // no-dd-sa:typescript-best-practices/boolean-prop-naming -- tRPC mutation-input DTO field mirroring the server `override` contract, not a React prop.
  approved: boolean;
}

export interface OverrideChangeRequestLevelInput {
  changeRequestId: string;
  /** The approval level to override. */
  levelId: string;
  comment: string | null;
  /** `true` → approve the level; `false` → reject. */
  // no-dd-sa:typescript-best-practices/boolean-prop-naming -- tRPC mutation-input DTO field mirroring the server `overrideLevel` contract, not a React prop.
  approved: boolean;
}

// Whole-CR override — one procedure + one document. Split into its own factory
// hook (createMutationHook holds exactly one procedure/document pair); the two
// override methods are recomposed in `useOverrideChangeRequest` below. Both
// paths return void — consumers ignore the payload. Cache: tRPC refreshes the
// CR feeds; Apollo evicts `change_request_by_pk` (parity with the old wrapper).
const useOverrideChangeRequestMutation = createMutationHook({
  name: 'overrideChangeRequest',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.changeRequest.override.mutationOptions({ onSuccess }),
  mapInput: (input: OverrideChangeRequestInput) => input,
  refreshChangeRequestFeedsWhen: () => true,
  mapResponse: (): void => undefined,
  graphqlDocument: OverrideChangeRequestByIdDocument,
  graphqlVariables: (input: OverrideChangeRequestInput) => ({
    Id: input.changeRequestId,
    // No `?? '-'` fallback: `comment` is non-nullable now, so the fallback was
    // dead code — and worse than dead, it was a trapdoor. If the caller's
    // validation were ever relaxed, `'-'` would sail through the new
    // mandatory-rationale bound and the requirement would be decorative again
    // (COI-622 A24).
    Rationale: input.comment,
    Approved: input.approved,
  }),
  mapGraphqlResponse: (): void => undefined,
  evictFields: ['change_request_by_pk'],
});

// Single-level override — its own procedure + document.
const useOverrideChangeRequestLevelMutation = createMutationHook({
  name: 'overrideChangeRequestLevel',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.changeRequest.overrideLevel.mutationOptions({ onSuccess }),
  mapInput: (input: OverrideChangeRequestLevelInput) => input,
  refreshChangeRequestFeedsWhen: () => true,
  mapResponse: (): void => undefined,
  graphqlDocument: UpdateApproverResponsesDocument,
  graphqlVariables: (input: OverrideChangeRequestLevelInput) => ({
    input: {
      ChangeRequestId: input.changeRequestId,
      Response: input.approved,
      // The Apollo UpdateApproverResponsesInput.Comment is non-null; the modal
      // always passes a constructed string, `?? ''` satisfies the type.
      Comment: input.comment ?? '',
      OverrideLevel: true,
      LevelId: input.levelId,
    },
  }),
  mapGraphqlResponse: (): void => undefined,
  evictFields: ['change_request_by_pk'],
});

/**
 * Flag-gated CR override (RSP-1528 §11). Composition shell over two
 * single-purpose factory hooks — the whole-CR override and the single-level
 * override — because `createMutationHook` holds exactly one procedure/document
 * pair. Consumers import THIS. `trpc` on → tRPC `changeRequest.override` /
 * `changeRequest.overrideLevel`; otherwise the legacy Apollo mutations. Errors
 * reject (no toast here) — the calling modal surfaces them, matching the rule
 * that data hooks don't own UI side-effects.
 */
export const useOverrideChangeRequest = () => {
  const { overrideChangeRequest, loading: overrideLoading } =
    useOverrideChangeRequestMutation();
  const { overrideChangeRequestLevel, loading: levelLoading } =
    useOverrideChangeRequestLevelMutation();

  return {
    overrideChangeRequest,
    overrideChangeRequestLevel,
    loading: overrideLoading || levelLoading,
  };
};
